// Step 5d Phase 3a — client API for conflicts + notifications.
// All online-only (resolution/read/dismiss change server authority). Callers gate on the
// connection state and disable rather than queue when offline (Phase 1/2 pattern).

import { apiFetch } from './api';

export type ConflictType = 'slot_clash' | 'edit_divergence';
export type ConflictStatus = 'open' | 'resolved';

export interface ConflictItem {
  id: string;
  type: ConflictType;
  task_id: string;
  other_task_id: string | null;
  sync_op_id: string;
  server_snapshot: unknown;
  offline_version: unknown;
  field_diff: Record<string, { from: unknown; to: unknown }> | null;
  status: ConflictStatus;
  resolver_id: string | null;
  resolution_code: string | null;
  resolution_note: string | null;
  created_at: string;
  resolved_at: string | null;
}

export type NotificationType =
  | 'conflict_slot_clash'
  | 'conflict_edit_divergence'
  | 'waitlist_contested'
  | 'device_revocation'
  | 'enquiry_escalation'
  | 'enquiry_assigned';

export interface AppNotification {
  id: string;
  type: NotificationType;
  conflict_item_id: string | null;
  task_id: string | null;
  enquiry_id: string | null; // deep-link to an enquiry (Enquiries module)
  title: string;
  message: string;
  created_at: string;
  read_at: string | null;
  dismissed_at: string | null;
}

// Resolution vocabulary (existing transitions only).
export const RESOLUTION_CODES: Record<ConflictType, { code: string; label: string; desc: string }[]> = {
  slot_clash: [
    { code: 'kept_offline', label: 'Keep this booking', desc: 'Accept the offline booking — it re-enters double-booking checks as a first-class claim. Nothing else is changed automatically.' },
    { code: 'cancelled_offline', label: 'Cancel this booking', desc: 'Cancel the offline booking; the booking it clashed with keeps the slot.' },
  ],
  edit_divergence: [
    { code: 'reapply_offline', label: 'Re-apply the offline edit', desc: 'Apply the offline changes over the current server version (runs the normal edit checks).' },
    { code: 'keep_server', label: 'Keep the server version', desc: 'Discard the offline edit; the booking stays as it currently is on the server.' },
  ],
};

export async function fetchNotifications(): Promise<{ items: AppNotification[]; unread: number } | null> {
  const res = await apiFetch<{ items: AppNotification[]; unread: number }>('/api/notifications');
  if (res.error || !res.data) return null;
  return res.data;
}

export async function markRead(id: string): Promise<void> {
  await apiFetch(`/api/notifications/${id}/read`, { method: 'POST' });
}
export async function dismissNotice(id: string): Promise<void> {
  await apiFetch(`/api/notifications/${id}/dismiss`, { method: 'POST' });
}

export async function fetchOpenConflicts(): Promise<ConflictItem[]> {
  const res = await apiFetch<ConflictItem[]>('/api/conflicts?status=open');
  return res.error || !res.data ? [] : res.data;
}

export async function fetchConflict(id: string): Promise<ConflictItem | null> {
  const res = await apiFetch<ConflictItem>(`/api/conflicts/${id}`);
  return res.error ? null : res.data;
}

export async function resolveConflict(id: string, code: string, note?: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiFetch<ConflictItem>(`/api/conflicts/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ resolution_code: code, ...(note ? { resolution_note: note } : {}) }),
  });
  return res.error ? { ok: false, error: res.error.message } : { ok: true };
}
